import { FC, useState, useEffect } from 'react'
import { useSessionHistory } from '../hooks/useSessionHistory'
import { useQueueActions } from '../hooks/useQueueActions'
import { SellerServiceSessionItem } from '../types'
import Swal from 'sweetalert2'

interface InServiceTabProps {
  pluginspaceId: number
}

export const InServiceTab: FC<InServiceTabProps> = ({ pluginspaceId }) => {
  const { sessions, loading, loadHistory } = useSessionHistory(pluginspaceId)
  const { finishSession, registerNoSaleReason, loading: actionLoading } =
    useQueueActions(pluginspaceId)
  const [inProgressSessions, setInProgressSessions] = useState<SellerServiceSessionItem[]>([])

  const noSaleReasons = [
    { value: 'size_unavailable', label: 'Tamanho indisponível' },
    { value: 'color_unavailable', label: 'Cor indisponível' },
    { value: 'price_too_high', label: 'Preço muito alto' },
    { value: 'did_not_find_product', label: 'Não encontrou o produto' },
    { value: 'just_browsing', label: 'Apenas navegando' },
    { value: 'customer_left', label: 'Cliente saiu' },
    { value: 'other', label: 'Outro' },
  ]

  useEffect(() => {
    loadHistory()
  }, [])

  useEffect(() => {
    const inProgress = sessions.filter((s) => s.status === 'in_progress')
    setInProgressSessions(inProgress)
  }, [sessions])

  const handleFinishSession = async (session: SellerServiceSessionItem) => {
    const { value: outcome } = await Swal.fire({
      title: 'Finalizar Atendimento',
      html: `
        <div class="mb-3">
          <label for="swal-outcome" class="form-label">Resultado do atendimento</label>
          <select id="swal-outcome" class="form-select">
            <option value="">Selecione...</option>
            <option value="sale_completed">Venda Concluída</option>
            <option value="pending_checkout">Esperando Checkout</option>
            <option value="no_sale">Sem Venda</option>
            <option value="abandoned">Abandonado</option>
          </select>
        </div>
      `,
      didOpen: () => {
        const select = Swal.getHtmlContainer()?.querySelector('#swal-outcome') as HTMLSelectElement
        if (select) select.focus()
      },
      preConfirm: () => {
        const select = Swal.getHtmlContainer()?.querySelector('#swal-outcome') as HTMLSelectElement
        const outcome = select?.value || ''
        if (!outcome) {
          Swal.showValidationMessage('Selecione um resultado')
          return null
        }
        return outcome
      },
      showCancelButton: true,
      confirmButtonText: 'Finalizar',
      cancelButtonText: 'Cancelar',
    })

    if (outcome) {
      // Se foi no_sale, pedir motivo
      if (outcome === 'no_sale') {
        const { value: reasonCode } = await Swal.fire({
          title: 'Motivo da Não Venda',
          html: `
            <div class="mb-3">
              <select id="swal-reason" class="form-select">
                <option value="">Selecione o motivo...</option>
                ${noSaleReasons.map((r) => `<option value="${r.value}">${r.label}</option>`).join('')}
              </select>
            </div>
          `,
          preConfirm: () => {
            const select = Swal.getHtmlContainer()?.querySelector('#swal-reason') as HTMLSelectElement
            const reason = select?.value || ''
            if (!reason) {
              Swal.showValidationMessage('Selecione um motivo')
              return null
            }
            return reason
          },
          showCancelButton: true,
          confirmButtonText: 'Confirmar',
          cancelButtonText: 'Cancelar',
        })

        if (reasonCode) {
          const duration = Math.round(
            (new Date(session.finishedAt || new Date()).getTime() -
              new Date(session.startedAt).getTime()) /
              1000
          )
          const success =
            (await finishSession(session.id, outcome, duration)) &&
            (await registerNoSaleReason(session.id, reasonCode))

          if (success) {
            Swal.fire('Sucesso', 'Atendimento finalizado', 'success')
            loadHistory()
          } else {
            Swal.fire('Erro', 'Erro ao finalizar atendimento', 'error')
          }
        }
      } else {
        const duration = Math.round(
          (new Date(session.finishedAt || new Date()).getTime() -
            new Date(session.startedAt).getTime()) /
            1000
        )
        const success = await finishSession(session.id, outcome, duration)
        if (success) {
          Swal.fire('Sucesso', 'Atendimento finalizado', 'success')
          loadHistory()
        } else {
          Swal.fire('Erro', 'Erro ao finalizar atendimento', 'error')
        }
      }
    }
  }

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return '-'
    const minutes = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${minutes}m ${secs}s`
  }

  if (loading) {
    return (
      <div className='d-flex justify-content-center align-items-center' style={{ minHeight: '300px' }}>
        <div className='spinner-border' role='status'>
          <span className='sr-only'>Carregando...</span>
        </div>
      </div>
    )
  }

  return (
    <div className='card'>
      <div className='card-header'>
        <h3 className='card-title'>Em Atendimento ({inProgressSessions.length})</h3>
      </div>
      <div className='card-body'>
        {inProgressSessions.length === 0 ? (
          <div className='text-center py-10'>
            <p className='text-muted'>Nenhum atendimento em progresso</p>
          </div>
        ) : (
          <div className='table-responsive'>
            <table className='table table-row-dashed table-row-gray-300 gy-7'>
              <thead>
                <tr className='fw-bold fs-6 text-gray-800'>
                  <th>Vendedor</th>
                  <th>Posição Inicial</th>
                  <th>Início</th>
                  <th>Duração</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {inProgressSessions.map((session) => (
                  <tr key={session.id}>
                    <td>
                      <span className='fw-bold'>{session.sellerName || 'Vendedor ' + session.sellerId}</span>
                    </td>
                    <td className='text-center'>{session.queuePositionAtStart}</td>
                    <td>
                      <small>
                        {new Date(session.startedAt).toLocaleTimeString('pt-BR')}
                      </small>
                    </td>
                    <td>
                      {(() => {
                        const duration = Math.round(
                          (new Date().getTime() - new Date(session.startedAt).getTime()) / 1000
                        )
                        return formatDuration(duration)
                      })()}
                    </td>
                    <td>
                      <span className='badge badge-light-info'>{session.status}</span>
                    </td>
                    <td>
                      <button
                        className='btn btn-sm btn-success'
                        onClick={() => handleFinishSession(session)}
                        disabled={actionLoading}
                      >
                        <i className='fas fa-check'></i> Finalizar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
